import styled, { keyframes } from 'styled-components'

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const FileItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:last-child {
    border-bottom: none;
  }
`

interface FileIconProps {
  $type?: string
}

// Helper function to get file type color from theme
const getFileTypeColor = ($type: string | undefined, theme: any): string => {
  if (!$type) return theme.colors.text.tertiary

  // PDF files
  if ($type.includes('pdf')) {
    return theme.colors.danger
  }

  // Excel/Spreadsheet files
  if (
    $type.includes('excel') ||
    $type.includes('spreadsheet') ||
    $type === 'application/vnd.ms-excel' ||
    $type ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return theme.colors.success
  }

  // Image files
  if ($type.includes('image')) {
    return theme.colors.primary
  }

  // CAD files
  if ($type.includes('dwg') || $type.includes('dxf')) {
    return theme.colors.warning
  }

  // Word/Document files
  if (
    $type.includes('word') ||
    $type === 'application/msword' ||
    $type ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return theme.colors.primaryLight
  }

  return theme.colors.text.tertiary
}

export const FileIcon = styled.div<FileIconProps>`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ $type, theme }) => getFileTypeColor($type, theme)};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  flex-shrink: 0;

  span {
    letter-spacing: -0.5px;
  }
`

export const FileContent = styled.div`
  flex: 1;
  min-width: 0;
`

export const FileName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.micro};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const FileDetails = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};

  span {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.micro};

    &:not(:last-child)::after {
      content: '•';
      margin-left: ${({ theme }) => theme.spacing.xsmall};
      color: ${({ theme }) => theme.colors.text.tertiary};
    }
  }
`

export const FileActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  ${FileItem}:hover & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    opacity: 1;
  }
`

// Delete confirmation overlay
export const DeleteConfirmOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.default};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  animation: ${slideDown} 0.2s ease-out;
  z-index: 10;
  border-radius: ${({ theme }) => theme.borderRadius.default};
`

export const DeleteConfirmText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  white-space: nowrap;
`

export const DeleteConfirmActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

interface DeleteConfirmButtonProps {
  $variant?: 'danger' | 'default'
}

export const DeleteConfirmButton = styled.button<DeleteConfirmButtonProps>`
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $variant, theme }) =>
    $variant === 'danger'
      ? `
    background: ${theme.colors.danger};
    color: white;
    border: none;

    &:hover {
      background: ${theme.colors.danger};
      opacity: 0.9;
    }
  `
      : `
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.default};

    &:hover {
      background: ${theme.colors.background.tertiary};
    }
  `}
`
